/**
 * Scheduled emails tab — §11-A, Wrangle-parity layout.
 *
 * Shows two groups of "scheduled" messages:
 *   1. Unsent drafts/queued threads (`sent_at IS NULL` and not bounced).
 *   2. Active follow-ups that will fire in the future (`next_followup_at`
 *      set, not `followup_stopped`, not yet replied to).
 *
 * Layout mirrors the Sent tab: list on the left, preview on the right
 * with `Edit Email` / `Cancel Email` CTAs. No dedicated `scheduled_for`
 * column exists today — we derive the send time from `next_followup_at`
 * (follow-ups) or `created_at` (queued drafts). When a proper scheduler
 * lands, swap the source query without touching this UI.
 */

import { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Edit3, XCircle, Inbox, Search, Briefcase } from 'lucide-react';
import type { EmailThread } from '@core/types';
import { emailService } from '@core/supabase/services/emailService';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { stripHtml } from '@core/utils/htmlPlain';
import { useToast } from '@shared/hooks/useToast';
import { EmailEmptyArt } from '../components/EmailEmptyArt';

// ── Wrangle-style scheduled chip ────────────────────────────────────────────
// Flatter pill (no status dot), tinted background + matching text — matches
// the "Blocked" chip style in Wrangle's scheduled emails reference.
function ScheduledChip({ kind }: { kind: 'followup' | 'draft' }) {
  const palette =
    kind === 'followup'
      ? { bg: 'rgba(245, 158, 11, 0.14)', text: '#d97706', label: 'Follow-up' }
      : { bg: 'var(--vscode-bg-hover)', text: 'var(--vscode-text-secondary)', label: 'Draft' };
  return (
    <span
      className="shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-medium"
      style={{ background: palette.bg, color: palette.text }}
    >
      {palette.label}
    </span>
  );
}

interface Props {
  threads: EmailThread[];
  loading: boolean;
  /** Called after a follow-up is cancelled so the parent can update its cache. */
  onThreadUpdated: (thread: EmailThread) => void;
}

type Scheduled = {
  thread: EmailThread;
  /** When the message is expected to go out. */
  dueAt: string;
  /** Human label for the chip (Follow-up vs Draft). */
  kind: 'followup' | 'draft';
};

function buildScheduled(threads: EmailThread[]): Scheduled[] {
  const now = Date.now();
  const out: Scheduled[] = [];
  for (const t of threads) {
    const hasUpcomingFollowup =
      !!t.next_followup_at &&
      !t.followup_stopped &&
      !t.cta_response &&
      new Date(t.next_followup_at).getTime() >= now;
    if (hasUpcomingFollowup && t.next_followup_at) {
      out.push({ thread: t, dueAt: t.next_followup_at, kind: 'followup' });
      continue;
    }
    const isDraft = !t.sent_at && !t.followup_stopped;
    if (isDraft) {
      out.push({ thread: t, dueAt: t.created_at, kind: 'draft' });
    }
  }
  return out.sort((a, b) => new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime());
}

export function EmailScheduledTab({ threads, loading, onThreadUpdated }: Props) {
  const { toast, showToast } = useToast();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const scheduled = useMemo(() => buildScheduled(threads), [threads]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scheduled;
    return scheduled.filter((s) => {
      const t = s.thread;
      const app = t.application as
        | { client?: { name?: string }; candidate?: { full_name?: string }; job?: { title?: string } }
        | undefined;
      const haystack = [
        t.subject,
        t.recipient_email,
        t.recipient_name,
        app?.client?.name,
        app?.candidate?.full_name,
        app?.job?.title,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [scheduled, search]);

  // Auto-select the first row on load (desktop only — list is full-height on mobile anyway)
  useEffect(() => {
    if (selectedId) return;
    if (filtered.length === 0) return;
    setSelectedId(filtered[0].thread.id);
  }, [filtered, selectedId]);

  const selected = useMemo(
    () => filtered.find((s) => s.thread.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  const handleCancel = async () => {
    if (!selected) return;
    setCancelling(true);
    try {
      await emailService.stopFollowups(selected.thread.id);
      const next: EmailThread = {
        ...selected.thread,
        followup_stopped: true,
        next_followup_at: null,
      };
      onThreadUpdated(next);
      showToast({ title: 'Follow-up cancelled.', kind: 'success' });
      setSelectedId(null);
    } catch (err) {
      showToast({
        title: err instanceof Error ? err.message : 'Could not cancel the follow-up.',
        kind: 'error',
      });
    } finally {
      setCancelling(false);
    }
  };

  if (!loading && scheduled.length === 0) {
    return (
      <div className="flex flex-col">
        <EmailEmptyArt
          leftIcon={Clock}
          rightIcon={Inbox}
          title="No scheduled emails"
          description="Emails scheduled for future delivery will appear here. You can preview, edit, or cancel them before they send."
        />
      </div>
    );
  }

  return (
    <>
      {toast}
      {/* Full-page Outlook-style two-column layout */}
      <div className="grid h-[calc(100vh-10rem)] gap-3 p-2 lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]">
        {/* ── LEFT PANEL — boxed with visible border ── */}
        <div
          className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border"
          style={{ borderColor: '#6B7280' }}
        >
          {/* Left panel toolbar */}
          <div
            className="shrink-0 border-b"
            style={{
              borderColor: '#6B7280',
              background: 'var(--vscode-bg-secondary)',
            }}
          >
            {/* Search */}
            <div className="flex items-center gap-2 px-3 py-2">
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 vscode-text-tertiary"
                aria-hidden
              />
              <input
                className="h-7 w-full rounded-[6px] border bg-transparent pl-7 pr-2 text-[12px] outline-none placeholder:vscode-text-tertiary"
                placeholder="Search emails"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  borderColor: 'var(--vscode-border-primary)',
                  color: 'var(--vscode-text-primary)',
                }}
              />
            </div>
            </div>
            {/* Count */}
            <div
              className="flex items-center gap-1.5 border-t px-3 py-1.5"
              style={{ borderColor: '#6B7280' }}
            >
              <Clock className="h-3.5 w-3.5 shrink-0 vscode-text-tertiary" aria-hidden />
              <span className="text-[10.5px] font-semibold uppercase tracking-wide vscode-text-tertiary">
                Scheduled
              </span>
              <span className="ml-auto text-[10.5px] tabular-nums vscode-text-tertiary">
                {filtered.length}
              </span>
            </div>
          </div>
          {/* Scrollable list */}
          <div
            className="min-h-0 flex-1 overflow-y-auto border-t"
            style={{ borderColor: '#6B7280' }}
          >
            {loading ? (
              <div className="p-3 space-y-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="h-14 rounded-lg animate-pulse vscode-bg-tertiary/70" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <p className="p-6 text-center text-xs vscode-text-tertiary">
                No scheduled emails match “{search}”.
              </p>
            ) : (
              filtered.map((s) => {
                const t = s.thread;
                const active = selectedId === t.id;
                const app = t.application as
                  | { candidate?: { full_name?: string }; job?: { title?: string } }
                  | undefined;
                const candidateName =
                  app?.candidate?.full_name?.trim() || t.recipient_name || '—';
                const jobTitle = app?.job?.title?.trim();
                const snippet = stripHtml(t.body_html || '')
                  .replace(/\s+/g, ' ')
                  .trim()
                  .slice(0, 110);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSelectedId(t.id)}
                    className={[
                      'w-full border-b border-[var(--vscode-border)]/55 px-3 py-2.5 text-left transition-colors',
                      active
                        ? 'bg-[color-mix(in_srgb,var(--vscode-text-link)_10%,transparent)]'
                        : 'hover:vscode-bg-hover',
                    ].join(' ')}
                    title={`Scheduled for ${formatDateTimeGmt(s.dueAt)}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="min-w-0 truncate text-[12.5px] font-semibold vscode-text-primary">
                        {t.subject || '—'}
                      </p>
                      <ScheduledChip kind={s.kind} />
                    </div>
                    {snippet ? (
                      <p className="mt-1 line-clamp-2 text-[11.5px] leading-snug vscode-text-secondary">
                        {snippet}
                      </p>
                    ) : null}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span
                        className="inline-flex max-w-[140px] items-center rounded-[6px] px-1.5 py-0.5 text-[10.5px] font-medium"
                        style={{
                          background: 'var(--vscode-bg-hover)',
                          color: 'var(--vscode-text-secondary)',
                        }}
                      >
                        <span className="truncate">{candidateName}</span>
                      </span>
                      {jobTitle ? (
                        <span
                          className="inline-flex max-w-[180px] items-center gap-1 rounded-[6px] px-1.5 py-0.5 text-[10.5px] font-medium"
                          style={{
                            background: 'color-mix(in srgb, #10B981 14%, transparent)',
                            color: '#10B981',
                          }}
                        >
                          <Briefcase className="h-2.5 w-2.5 shrink-0" aria-hidden />
                          <span className="truncate">{jobTitle}</span>
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── RIGHT PANEL — boxed with visible border ── */}
        <div
          className="hidden min-h-0 min-w-0 overflow-hidden rounded-lg border lg:flex lg:flex-col"
          style={{ borderColor: '#6B7280' }}
        >
          {selected ? (
            <>
              {/* Top toolbar */}
              <div
                className="flex shrink-0 items-center gap-2 border-b px-3 py-2"
                style={{
                  borderColor: '#6B7280',
                  background: 'var(--vscode-bg-secondary)',
                }}
              >
                <Link
                  to={`/emails/compose?applicationId=${encodeURIComponent(
                    selected.thread.application_id,
                  )}&replyToThread=${encodeURIComponent(selected.thread.id)}`}
                  className="inline-flex h-7 items-center gap-1.5 rounded-[6px] px-3 text-[11px] font-medium text-white"
                  style={{ background: '#10B981' }}
                >
                  <Edit3 className="h-3.5 w-3.5" />
                  Edit Email
                </Link>
                {selected.kind === 'followup' ? (
                  <button
                    type="button"
                    onClick={() => void handleCancel()}
                    disabled={cancelling}
                    className="inline-flex h-7 items-center gap-1.5 rounded-[6px] border px-2 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)] disabled:opacity-60"
                    style={{
                      borderColor: 'var(--vscode-border-primary)',
                      color: '#EF4444',
                    }}
                  >
                    <XCircle className="h-3 w-3" />
                    {cancelling ? 'Cancelling…' : 'Cancel'}
                  </button>
                ) : null}
                <span className="ml-auto text-[10.5px] tabular-nums vscode-text-tertiary">
                  {formatDateTimeGmt(selected.dueAt)}
                </span>
              </div>
              {/* Header — To recipient + status */}
              <div
                className="flex shrink-0 flex-col gap-0.5 border-b px-3 py-2"
                style={{ borderColor: '#6B7280' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="min-w-0 truncate text-[12px]">
                    <span className="vscode-text-tertiary">To </span>
                    <span className="font-medium vscode-text-primary">
                      {selected.thread.recipient_name || selected.thread.recipient_email}
                    </span>
                  </p>
                  <ScheduledChip kind={selected.kind} />
                </div>
                {selected.thread.subject ? (
                  <h2 className="truncate text-[12px] font-semibold leading-snug vscode-text-primary">
                    {selected.thread.subject}
                  </h2>
                ) : null}
              </div>
              {/* Body — scrollable */}
              <div className="min-h-0 min-w-0 flex-1 overflow-auto px-4 py-4">
                <div
                  className={[
                    'text-[13px] leading-[1.6] vscode-text-primary',
                    '[&_*]:!max-w-full',
                    '[&_table]:!w-full [&_table]:!max-w-full [&_table]:!border-collapse',
                    '[&_td]:!max-w-full [&_td]:break-words',
                    '[&_img]:!h-auto [&_img]:!max-w-full',
                    '[&_pre]:whitespace-pre-wrap [&_pre]:break-words',
                    '[&_a]:text-[var(--vscode-text-link)] [&_a]:underline',
                    '[&_p]:my-1.5',
                  ].join(' ')}
                  dangerouslySetInnerHTML={{
                    __html:
                      selected.thread.body_html ||
                      '<p style="color:var(--vscode-text-tertiary)">No body yet.</p>',
                  }}
                />
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center">
              <Clock className="mb-3 h-10 w-10 opacity-60 vscode-text-tertiary" aria-hidden />
              <p className="text-sm font-medium vscode-text-primary">Select a scheduled email</p>
              <p className="mt-1 max-w-xs text-xs vscode-text-secondary">
                Preview the content, edit the draft, or cancel a pending follow-up before it sends.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
